// Shared table methods for every module page (CrudTest, Roles, Users ...).
// A page uses it with:  mixins: [tableMixin, yourPageMixin]
//
// What the page must have:
//   - data: apiUrl           e.g. "/crud/api_crud.php" (used by fetchRecords, delete, import, export)
//   - method: getDefaultTableData() if it needs more than the standard table data
//     (e.g. { ...this.baseTableData(), searchBy: "firstname", statuses: [] })
//   tableData, defaultAlert, formImportData, isSaving, searchTimer ... come from this mixin.
//
// Optional page data:
//   - standardPageSetup: true   Enter-to-save + table shortcuts set up here
//   - usesTags: true            tag list + "Add tag" through apiUrl
//   - columnsKey                saves the hidden columns, e.g. "crudtest"
//   - extraFilterKeys           extra filters in the Filter window, e.g. ["statuses", "tagIds"]
//   - exportFileName            name of the exported file, e.g. "crudTestExport"
//   - importTemplateFileName    e.g. "crudtest_import_template.xlsx"
//
// Optional page hooks:
//   - afterFetch(response)   runs after the table loads (e.g. read extra lists from the response)
//   - afterDelete(response)  runs after a delete / restore succeeds
//
// If a page needs something different, write a method with the same name in that
// page (or its own mixin). The page's version wins over this one.
import axios from "axios";
import { datePresetLabel } from "../components/admin/datePresets.js";
import { cachedGet, outboxItems, discardOutboxItem, addToOutbox, forgetOutboxItem, newClientUuid } from "../offline/offlineStore.js";

export default {
    data() {
        return {
            // Standard page data. A page that lists the same name in its own data keeps its own.
            // getDefault...() below can also be replaced by the page (e.g. CrudTest's import data).
            submitted: false,
            isEditing: false,
            isSaving: false,
            expandedRows: {},                 // (+) details that are open
            searchTimer: null,                // waits until the user stops typing
            lastKnownPage: null,
            deleteModalTitle: this.$deleteModalText.modalTitleSoft,
            deleteModalMessage: this.$deleteModalText.modalMessageSoft,
            defaultAlert: this.getDefaultAlert(),
            formImportData: this.getDefaultFormImportData(),
            formExportData: this.getDefaultFormExportData(),
            tableData: this.getDefaultTableData(),

            // The Filter window edits this copy. "Apply filters" copies it to tableData.
            filterDraft: {},
            // Columns the user has hidden (Columns button). Saved per page with data: columnsKey
            hiddenColumns: [],
            // Tag list for the Tags boxes. Loaded when the page has usesTags (or its own tagsApi)
            tagOptions: [],
            // Offline: true while the table shows rows saved on this device (not fresh from the server)
            showingSavedData: false,
            // Offline saving. A page switches it on with data: offlineSave: true (and saves with sendSave)
            offlineSave: false,
            offlineClientUuid: "",   // device-made ID of the new record in the form (stops double saves)
            editingDateUpdated: "",  // date_updated of the record in the form (spots changes by someone else)
            // True while the table is loading (the page shows a placeholder row as tall as a full page)
            tableLoading: false,

            // Height of one table row (measured once rows are on screen and remembered; 50px until then)
            tableRowHeight: Number(localStorage.getItem("tableRowHeight")) || 50,
            // The row that was just saved: it blinks for a moment (see flashRow)
            justSavedRecordId: null,
            justSavedTimer: null,
        };
    },
    created() {
        // Pages with tags (data: usesTags, or their own tagsApi): load the tag list
        this.loadTagOptions();

        // Load this page's hidden columns (pages without columnsKey are not affected)
        if (!this.columnsKey) return;
        try {
            const saved = JSON.parse(localStorage.getItem(`tableColumns:${this.columnsKey}`) || "[]");
            this.hiddenColumns = Array.isArray(saved) ? saved : [];
        } catch (e) {
            this.hiddenColumns = [];
        }
    },
    watch: {
        // Import finished (any result): stop the fake progress timer
        "formImportData.uploading"(uploading) {
            if (!uploading) this.stopSimulatedProgress();
        },
        // Tick "select all" when every row on the page is ticked
        "tableData.selectedRecordIds"(val) {
            const records = this.tableData?.records || [];
            this.tableData.selectAll = val.length > 0 && val.length === records.length;
        },
        // Remember the hidden columns for next time
        hiddenColumns(value) {
            if (!this.columnsKey) return;
            try {
                localStorage.setItem(`tableColumns:${this.columnsKey}`, JSON.stringify(value));
            } catch (e) {
                // private window or storage blocked: it just won't be remembered
            }
        },
    },
    mounted() {

        // Pages with data: standardPageSetup = true get Enter-to-save and the table shortcuts here
        if (this.standardPageSetup) {
            this.$enableModalEnterSave("#recordFormModal", "#recordForm");
            this.$enableTableShortcuts();
        }

        // Offline: the table shows saved rows. When the internet comes back, load fresh ones
        this.onBackOnline = () => {
            if (this.showingSavedData) this.fetchRecords();
        };
        window.addEventListener("online", this.onBackOnline);

        // Offline saves were sent: show the table as the server now has it
        this.onOutboxSynced = (event) => {
            this.fetchRecords();
            // Swap the yellow "saved on this device" message for a green one (green disappears by itself)
            const sent = event.detail?.sent || 0;
            if (sent) this.handleAlert("ending", `${sent} offline save(s) sent.`, "success", "page");
        };
        window.addEventListener("outbox-synced", this.onOutboxSynced);
        // Saves made offline earlier and still waiting: send them now if there is internet
        if (this.$offlineEnabled && navigator.onLine) this.$syncOutbox();

        // Form window closed: the next Add gets a new device-made ID
        this.onFormModalHidden = () => {
            this.offlineClientUuid = "";
            this.editingDateUpdated = "";
        };
        document.getElementById("recordFormModal")?.addEventListener("hidden.bs.modal", this.onFormModalHidden);
    },
    beforeUnmount() {
        // Leaving the page: don't leave a timer running
        this.stopSimulatedProgress();
        clearTimeout(this.searchTimer);
        clearTimeout(this.justSavedTimer);
        window.removeEventListener("online", this.onBackOnline);
        window.removeEventListener("outbox-synced", this.onOutboxSynced);
        document.getElementById("recordFormModal")?.removeEventListener("hidden.bs.modal", this.onFormModalHidden);
        if (this.standardPageSetup) {
            this.$disableModalEnterSave();
            this.$disableTableShortcuts();
        }
    },
    computed: {
        // Address for the tag list and "Add tag": the page's own tagsApi, or its apiUrl when usesTags is on.
        // Empty = the page has no tags (nothing is loaded).
        tagsEndpoint() {
            if (this.tagsApi) return this.tagsApi;
            return this.usesTags ? this.apiUrl || "" : "";
        },
        // How many filters are on (Filter button). A page can add its own with computed: pageFilterCount.
        // A page with its own activeFilterCount keeps it (the page's version wins).
        activeFilterCount() {
            const t = this.tableData;
            if (!t) return 0;
            let count = 0;
            if (t.recordFilter && t.recordFilter !== "all") count++;
            if (typeof this.getDefaultTableData === "function" && t.searchBy !== this.getDefaultTableData().searchBy) count++;
            if (t.startDate || t.endDate) count++;
            if (Array.isArray(t.statuses) && t.statuses.length) count++;
            if (Array.isArray(t.tagIds) && t.tagIds.length) count++;
            return count + (this.pageFilterCount || 0);
        },
        // Labels for the "Filtered by:" row (ActiveFilters component)
        activeFilterChips() {
            const t = this.tableData;
            if (!t) return [];
            const chips = [];

            // Date range
            if (t.startDate || t.endDate) {
                const day = (v) => (v ? v.replace(/^(\d{4})-(\d{2})-(\d{2}).*$/, "$3/$2/$1") : "…");
                const dayTime = (v) => (v ? v.replace(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}).*$/, "$3/$2/$1 $4") : "…");
                const preset = t.datePreset && t.datePreset !== "custom" && t.datePreset !== "all" ? t.datePreset : "";

                chips.push({
                    key: "date",
                    icon: "fa fa-calendar",
                    label: preset
                        ? `${datePresetLabel(preset, t.dateYear)} (${day(t.startDate)} – ${day(t.endDate)})`
                        : `${dayTime(t.startDate)} → ${dayTime(t.endDate)}`,
                });
            }

            // Status (pages with tableData.statuses and data: statusOptions)
            if (Array.isArray(t.statuses) && t.statuses.length) {
                const names = t.statuses.map((v) => (this.statusOptions || []).find((o) => o.value === v)?.label || v);
                chips.push({ key: "status", icon: "fa fa-flag", label: `Status: ${names.join(", ")}` });
            }

            // Tags (pages with tableData.tagIds and a tag list)
            if (Array.isArray(t.tagIds) && t.tagIds.length) {
                const names = t.tagIds.map((id) => (this.tagOptions || []).find((tag) => tag.id === id)?.name || `#${id}`);
                chips.push({ key: "tags", icon: "fa fa-tags", label: `Tags: ${names.join(", ")}` });
            }

            // View (All / By me / Deleted). A page can add its own names with data: recordFilterLabels
            if (t.recordFilter && t.recordFilter !== "all") {
                const labels = { byme: "Records by me", deletedone: "Deleted records", ...(this.recordFilterLabels || {}) };
                chips.push({ key: "view", icon: "fa fa-eye", label: `View: ${labels[t.recordFilter] || t.recordFilter}` });
            }

            // Search box (only once the search has really run)
            if (t.appliedSearchQuery) {
                chips.push({
                    key: "search",
                    icon: "fa fa-search",
                    label: `${this.$transformUnderscoreText(t.appliedSearchBy || t.searchBy)}: "${t.appliedSearchQuery}"`,
                });
            }

            // The page's own filters (page computed: pageFilterChips)
            chips.push(...(this.pageFilterChips || []));

            return chips;
        },
    },
    methods: {
        /* ---------------- Defaults (a page can replace any of these) ---------------- */
        // Standard table data. A page adds its own on top:
        //   getDefaultTableData() { return { ...this.baseTableData(), searchBy: "firstname", statuses: [] }; }
        baseTableData() {
            return {
                records: [],
                currentPage: 1,
                totalPages: 1,
                recordsPerPage: this.$recordsPerPage,
                searchBy: "",
                searchQuery: "",
                searchPlaceHolderText: "Search",
                responseTime: null,
                startRecord: 0,
                endRecord: 0,
                currentSortColumn: "id",
                currentSortDirection: "DESC",
                recordFilter: "all",
                datePreset: "all",                    // date range in the Filter window
                dateYear: new Date().getFullYear(),   // year for First...Fourth quarter
                startDate: "",
                endDate: "",
                selectedRecordId: "",
                deleteType: "",                       // soft / restore / permanent
                totalRecords: 0,
                isVisible: false,
                loaderMessage: this.$loaderMessages.loaderMessageDefault,
                loaderValue: null,
                simulatedProgressInterval: null,
                selectAll: false,
                selectedRecordIds: [],
            };
        },
        getDefaultTableData() {
            return this.baseTableData();
        },
        getDefaultAlert() {
            return {
                alertMessage: "",
                alertstatus: "",
                showAlert: "",
                alertType: "",
                button_text: "",
                pageOrModal: "",
            };
        },
        getDefaultFormImportData() {
            return {
                dataFile: null,
                templateLoading: false, // spinner on the Download Import Template button
                importErrors: [],       // rows that were not imported, and why
                uploading: false,
                progress: 0,
                message: "",
                messageType: "",
            };
        },
        getDefaultFormExportData() {
            return {
                exportProgress: 0,
            };
        },

        /* ---------------- Alerts & errors ---------------- */
        handleAlert(daOrigin, daMessage, daClass, daPageOrModal) {
            this.$displayAlert(daOrigin, daMessage, daClass, daPageOrModal);
        },
        handleError(daOrigin, error, daClass, daPageOrModal) {
            this.button_text = "";
            this.alertType = "danger";
            if (error && error.data && error.data.message) {
                this.defaultAlert.alertMessage = error.data.message;
            } else {
                this.defaultAlert.alertMessage = error;
            }
            this.showAlert = true;
            this.handleAlert(daOrigin, this.defaultAlert.alertMessage, daClass, daPageOrModal);
            console.error("Error:", this.defaultAlert.alertMessage);
        },
        // One message for every failed request. place = "page" or "modal"
        handleRequestError(error, place = "page") {
            if (place === "page" && this.tableData) this.tableData.isVisible = false;
            this.handleAlert(
                "ending",
                error && error.message === "Network Error"
                    ? "No internet connection. Please check your network."
                    : "Something went wrong. Please try again later.",
                "danger",
                place
            );
        },

        /* ---------------- Loading the table ---------------- */
        // Loads one page of the table
        async fetchRecords(
            page = this.tableData.currentPage,
            orderBy = this.tableData.currentSortColumn,
            orderDirection = "DESC",
            startDate = this.tableData.startDate,
            endDate = this.tableData.endDate,
            loadingMessage = this.$loaderMessages.loaderMessageDefault
        ) {
            this.$showOrHideLoader(loadingMessage);
            const t = this.tableData;

            // While loading: the page shows a placeholder row as tall as a full page (see loadingRowHeight)
            this.tableLoading = true;

            // Dates, statuses, tags ... come from filterQueryString()
            const url =
                `${this.apiUrl}?action=fetchRecords&page=${page}&limit=${t.recordsPerPage}` +
                `&searchBy=${t.searchBy}&search=${encodeURIComponent(t.searchQuery)}` +
                `&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${t.recordFilter}` +
                this.filterQueryString(startDate, endDate);

            try {
                // Offline: gives back the rows saved the last time this view loaded online
                const { response, savedAt } = await cachedGet(url, this.$handleConfig(), this.$offlineEnabled);
                this.showingSavedData = !!savedAt;
                this.$offlineDataBanner(savedAt);

                if (response.data.status == "success") {
                    this.handleFetchSuccess(response);
                    if ("lastKnownPage" in this.$data) {
                        this.lastKnownPage = response.data.results.hasNextPage ? null : response.data.results.current_page;
                    }
                    if (typeof this.afterFetch === "function") this.afterFetch(response);
                    // Offline saves not sent yet: show them in the table
                    await this.applyPendingRecords();
                } else {
                    if (["JWT_EXPIRED", "CSRF_INVALID", "AUTH_REQUIRED"].includes(response.data?.code)) {
                        this.$forceLogOut("danger", response.data.message);
                        return;
                    }
                    this.tableData.isVisible = false;
                    this.handleAlert("starting", response.data.message, "danger", "page");
                }
            } catch (error) {
                // Offline, and this page / these filters were never opened online: say so, keep the rows on screen
                if (this.$offlineEnabled && !error.response) {
                    this.tableData.isVisible = false;
                    await this.applyPendingRecords();
                    this.handleAlert(
                        "ending",
                        "You're offline. This page (with these filters) wasn't opened while online, so there's nothing saved to show.",
                        "warning",
                        "page"
                    );
                    return;
                }
                this.handleRequestError(error, "page");
            } finally {
                // Rows are on screen (or an error was shown): no more placeholder
                this.tableLoading = false;
                this.measureTableRowHeight();
            }
        },
        // "Last" button in the pagination
        async goToLastPage() {
            this.$showOrHideLoader(this.$loaderMessages.loaderMessageDefault);
            const t = this.tableData;

            try {
                const url =
                    `${this.apiUrl}?action=getLastPage&page=1&limit=${t.recordsPerPage}` +
                    `&searchBy=${t.searchBy}&search=${encodeURIComponent(t.searchQuery)}` +
                    `&orderBy=${t.currentSortColumn}&orderDirection=DESC&filter=${t.recordFilter}` +
                    this.filterQueryString();

                const { response } = await cachedGet(url, this.$handleConfig(), this.$offlineEnabled);

                if (response.data.status === "success") {
                    const lastPage = response.data.results.lastPage;
                    await this.fetchRecords(lastPage, t.currentSortColumn, "DESC");
                    if ("lastKnownPage" in this.$data) this.lastKnownPage = lastPage;
                    t.totalPages = lastPage;
                } else {
                    this.tableData.isVisible = false;
                    this.handleAlert("ending", response.data.message || "Unable to find the last page.", "danger", "page");
                }
            } catch (error) {
                this.tableData.isVisible = false;
                this.handleAlert("ending", "Unable to find the last page. Please try again.", "danger", "page");
            }
        },
        handleFetchSuccess(response) {
            const startTime = Date.now();
            this.tableData.records = response.data.results.records;
            // Keep only ticks on rows still on screen (another page / filter: the count stays honest)
            this.tableData.selectedRecordIds = this.tableData.selectedRecordIds.filter((id) =>
                this.tableData.records.some((r) => r.id === id)
            );
            this.tableData.currentPage = response.data.results.current_page;
            this.tableData.totalPages = response.data.results.total_pages;
            // Only pages that keep a "last known page" (e.g. CrudTest)
            if ("lastKnownPage" in this.$data && this.tableData.totalRecords > 0) {
                this.lastKnownPage = this.tableData.totalPages;
            }
            this.tableData.hasNextPage = response.data.results.hasNextPage;
            this.tableData.totalRecords = response.data.results.totalRecords;
            this.tableData.startRecord = response.data.results.startRecord;
            this.tableData.endRecord = response.data.results.endRecord;
            this.tableData.isVisible = false;
            const endTime = Date.now();
            this.tableData.responseTime = endTime - startTime;
            console.log(this.tableData.responseTime);
        },
        updateLoaderMessage(message) {
            this.tableData.loaderMessage = message;
        },
        // Remember how tall one row is on this screen (used by the filler row)
        measureTableRowHeight() {
            this.$nextTick(() => {
                const row = document.querySelector("#bootstrapTable tbody tr.clickable-row");
                if (!row?.offsetHeight) return;
                this.tableRowHeight = row.offsetHeight;
                try {
                    localStorage.setItem("tableRowHeight", row.offsetHeight);
                } catch (e) {
                    // storage blocked: the 50px default is used next time
                }
            });
        },
        // Height for the empty table message: a full page of rows (records per page),
        // but never smaller than 220px or bigger than 400px (100 per page would otherwise be huge)
        fullPageHeight() {
            const fullPage = this.tableRowHeight * (Number(this.tableData.recordsPerPage) || 0);
            return Math.min(Math.max(fullPage, 220), 400) + "px";
        },
        // Offline saves waiting to be sent, shown in the table:
        // a changed record shows its new values; a new record shows at the top of page 1
        async applyPendingRecords() {
            if (!this.$offlineEnabled || !this.apiUrl) return;
            const items = await outboxItems(this.apiUrl);
            if (!items.length) return;

            const records = [...this.tableData.records];
            items.forEach((item) => {
                const pending = {
                    ...item.preview,
                    _pending: true,                     // "Waiting to sync" badge
                    _error: item.error || "",           // "Not sent" badge (the server refused it)
                    _clientUuid: item.clientUuid,
                    _dynamicFields: item.dynamicFields,
                    _outboxKey: item.key,               // used by "Discard offline change"
                };
                if (item.recordId) {
                    const i = records.findIndex((r) => String(r.id) === String(item.recordId));
                    // _serverDateUpdated: the version that was edited (used to spot changes by someone else)
                    if (i >= 0) records[i] = { ...records[i], ...pending, id: records[i].id, _serverDateUpdated: records[i].date_updated };
                } else if (this.tableData.currentPage === 1) {
                    records.unshift({ ...pending, id: `offline-${item.clientUuid}` });
                }
            });
            this.tableData.records = records;
        },
        /* ---------------- Offline saving (pages with data: offlineSave = true) ---------------- */
        // Every page's Save sends with this instead of axios.post(this.apiUrl, formData, ...).
        // Online: posts as before and gives back the server's answer.
        // Offline: keeps the save on this device and gives back { offline: true } (the page then does nothing more).
        async sendSave(formData, url = this.apiUrl) {
            const offline = this.offlineSave && this.$offlineEnabled;
            const id = this.formInputs?.id || "";

            // New record: a device-made ID, so the server can spot it if it arrives twice
            if (offline && !id) {
                if (!this.offlineClientUuid) this.offlineClientUuid = newClientUuid();
                formData.set("client_uuid", this.offlineClientUuid);
            }

            if (offline && !navigator.onLine) return this.keepSaveOffline(formData, url);

            try {
                const response = await axios.post(url, formData, this.$handleConfig());
                // Saved online: an older offline copy of this record must not be sent later
                if (offline && response.data && response.data.status === "success") {
                    forgetOutboxItem(url, id, this.offlineClientUuid);
                }
                return response;
            } catch (error) {
                // The internet dropped while saving: keep it on this device instead
                if (offline && !error.response) return this.keepSaveOffline(formData, url);
                throw error;
            }
        },
        // Keeps one save on this device (the outbox). It is sent when the internet is back.
        async keepSaveOffline(formData, url = this.apiUrl) {
            const id = this.formInputs?.id || "";
            formData.set("offline_saved_at", new Date().toISOString()); // when it was really saved
            // Edit: the version it was made on. The server refuses it if someone else changed the record since.
            if (id && this.editingDateUpdated) formData.set("expected_date_updated", this.editingDateUpdated);

            const kept = await addToOutbox({
                apiUrl: url,
                formData,
                recordId: id,
                clientUuid: this.offlineClientUuid,
                preview: this.offlinePreview(),
                // A page's extra form data to bring back when the waiting save is edited (e.g. CrudTest's Dynamic Fields)
                dynamicFields: typeof this.offlineExtraData === "function" ? this.offlineExtraData() : null,
            });
            if (!kept) {
                this.handleAlert("ending", "You're offline and this save couldn't be kept on the device. Please try again when you're online.", "danger", "modal");
                return { offline: true, kept: false };
            }
            this.defaultAlert.submitingSavingRecord = false;
            this.$hideModal("recordFormModal");
            await this.fetchRecords();
            this.handleAlert("ending", "You're offline. The record is saved on this device and will be sent when the internet is back.", "warning", "page");
            return { offline: true, kept: true };
        },
        // Edit button: gives back the id to put in the form, and remembers what offline saving needs.
        // A new record saved offline and not sent yet has no server id: "" (it is still a new record).
        startOfflineEdit(record) {
            const isPendingNew = !!record._pending && String(record.id).startsWith("offline-");
            this.offlineClientUuid = isPendingNew ? record._clientUuid : "";
            this.editingDateUpdated = isPendingNew ? "" : (record._serverDateUpdated ?? record.date_updated ?? "");
            return isPendingNew ? "" : (record.id ?? "");
        },
        // How an offline save looks in the table until it is sent: the form's values.
        // A page can write its own (e.g. CrudTest turns tag ids into tag names).
        offlinePreview() {
            const now = new Date().toISOString().slice(0, 19).replace("T", " ");
            const preview = {};
            Object.entries(this.formInputs || {}).forEach(([key, value]) => {
                if (key === "id" || /password/i.test(key) || value instanceof Blob) return;
                preview[key] = value;
            });
            preview.date_updated = now;
            if (!this.formInputs?.id) preview.date_created = now;
            return preview;
        },
        // Actions → "Discard offline change": throw away a save that wasn't sent
        async discardOfflineSave(record) {
            if (!confirm("Discard this offline change? It will not be sent.")) return;
            await discardOutboxItem(record._outboxKey);
            this.fetchRecords();
            this.handleAlert("ending", "The offline change was discarded.", "info", "page");
        },

        /* ---------------- Search ---------------- */
        onSearchInput() {
            clearTimeout(this.searchTimer);

            // Button mode: typing doesn't search, but clearing the box reloads all records straight away
            if (this.$searchWithButtonClick) {
                if (this.tableData.searchQuery.trim() === "") {
                    this.runSearch();
                }
                return;
            }

            // Type mode: show the loader while the user is typing
            this.tableData.loaderMessage = this.$loaderMessages.searching;
            this.tableData.loaderValue = "";
            this.tableData.isVisible = true;

            // Decide what to do once the user stops typing for half a second
            this.searchTimer = setTimeout(() => {
                const query = this.tableData.searchQuery.trim();

                // Too short to search (1 character): just hide the loader
                if (query.length > 0 && query.length < 2) {
                    this.tableData.isVisible = false;
                    return;
                }

                this.runSearch();
            }, 500);
        },
        // Search button and Enter key (button mode only; type mode searches by itself)
        onSearchButtonClick() {
            if (!this.$searchWithButtonClick) return;
            clearTimeout(this.searchTimer);
            this.runSearch(); // any length, even 1 character
        },
        // Runs the search from page 1
        runSearch() {
            // Hand the loader over to fetchRecords: it shows it and hides it when the results arrive
            this.tableData.isVisible = false;

            // Pages with the date range in the Filter window keep their dates when searching.
            // Older pages (with the Date Range Report window) still clear them.
            if (!("datePreset" in this.tableData)) {
                this.tableData.startDate = "";
                this.tableData.endDate = "";
                this.$clearValidationStates("#reportFormGenerator");
            }
            // What was really searched (for the "Filtered by:" label)
            this.tableData.appliedSearchQuery = this.tableData.searchQuery.trim();
            this.tableData.appliedSearchBy = this.tableData.searchBy;
            this.tableData.currentPage = 1;
            this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.searching
            );
        },
        // Search By changed: close the filter modal and get the search box ready
        changePlaceholder() {
            this.tableData.searchPlaceHolderText = "Search by " + this.$transformUnderscoreText(this.tableData.searchBy);

            const modal = document.getElementById("filterDataModal");

            // After the modal has closed, put the cursor in the search box
            modal?.addEventListener("hidden.bs.modal", () => {
                document.getElementById("search")?.focus();
            }, { once: true });

            this.$hideModal("filterDataModal");

            // Something already typed: search again using the new column
            if (this.tableData.searchQuery) {
                this.onSearchInput();
            }
        },

        /* ---------------- Filters ---------------- */
        // Filter changed: close the filter modal and reload from page 1
        onFilterChange() {
            this.$hideModal("filterDataModal");

            this.tableData.currentPage = 1;
            this.fetchRecords(
                1,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault
            );
        },
        // The filters the Filter window can change. A page can add more with data: extraFilterKeys
        filterKeys() {
            return ["recordFilter", "datePreset", "dateYear", "startDate", "endDate", ...(this.extraFilterKeys || [])];
        },
        // Filter button: copy the current filters into the draft the window edits
        openFilters() {
            const draft = {};
            this.filterKeys().forEach((key) => {
                const value = this.tableData[key];
                draft[key] = Array.isArray(value) ? [...value] : value; // copy lists (status, tags) too
            });
            this.filterDraft = draft;
            this.defaultAlert = this.getDefaultAlert();
        },
        // Filter window: "Apply filters" button. Only now does the table change
        applyFilters() {
            const d = this.filterDraft;
            if (d.datePreset === "custom" && (!d.startDate || !d.endDate)) {
                this.handleAlert("starting", "Please choose both a start and an end date.", "danger", "modal");
                return;
            }
            if (d.startDate && d.endDate && d.startDate > d.endDate) {
                this.handleAlert("starting", "The start date must be before the end date.", "danger", "modal");
                return;
            }
            this.filterKeys().forEach((key) => {
                this.tableData[key] = d[key];
            });
            this.defaultAlert = this.getDefaultAlert();
            this.onFilterChange(); // closes the window and reloads from page 1
        },
        // Filter window: "Clear filters" button (also used by "Clear all")
        clearFilters() {
            const defaults = this.getDefaultTableData();
            this.filterKeys().forEach((key) => {
                this.tableData[key] = defaults[key];
            });
            this.openFilters(); // the draft matches the cleared filters
            this.onFilterChange();
        },
        // Filters sent with every request (table, Last page button, export).
        // A page can add its own with a method: pageFilterParams() { return { modules: "crud" }; }
        filterParams(startDate = this.tableData.startDate, endDate = this.tableData.endDate) {
            const t = this.tableData;
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (Array.isArray(t.statuses) && t.statuses.length) params.statuses = t.statuses.join(",");
            if (Array.isArray(t.tagIds) && t.tagIds.length) params.tags = t.tagIds.join(",");
            if (typeof this.pageFilterParams === "function") Object.assign(params, this.pageFilterParams());
            return params;
        },
        // The filters as "&startDate=...&statuses=..." for a GET url
        filterQueryString(startDate, endDate) {
            const params = this.filterParams(startDate, endDate);
            return Object.keys(params)
                .map((key) => {
                    // Dates stay as they are: the axios interceptor (app file) changes them to UTC
                    const value = key === "startDate" || key === "endDate" ? params[key] : encodeURIComponent(params[key]);
                    return `&${key}=${value}`;
                })
                .join("");
        },
        // The filters added to a FormData (export)
        appendFilterFormData(formData) {
            const params = this.filterParams();
            Object.keys(params).forEach((key) => formData.append(key, params[key]));
            return formData;
        },
        // × on one label in the "Filtered by:" row
        removeFilterChip(key) {
            if (key === "date") {
                this.tableData.datePreset = "all";
                this.tableData.startDate = "";
                this.tableData.endDate = "";
            }
            if (key === "status") {
                this.tableData.statuses = [];
            }
            if (key === "tags") {
                this.tableData.tagIds = [];
            }
            if (key === "view") {
                this.tableData.recordFilter = "all";
            }
            if (key === "search") {
                this.tableData.searchQuery = "";
                this.tableData.appliedSearchQuery = "";
            }
            // The page's own filters (page method: removePageFilterChip)
            if (typeof this.removePageFilterChip === "function") {
                this.removePageFilterChip(key);
            }
            this.onFilterChange(); // reload from page 1
        },
        // "Clear all" in the "Filtered by:" row: filters and search box
        clearAllFilterChips() {
            this.tableData.searchQuery = "";
            this.tableData.appliedSearchQuery = "";
            this.clearFilters();
        },

        /* ---------------- Sort & page size ---------------- */
        async orderBy(columnName) {
            this.tableData.loaderMessage = this.$loaderMessages.ordering;
            this.tableData.loaderValue = "";
            // Same column: flip the direction. New column: start with ASC
            if (this.tableData.currentSortColumn === columnName) {
                this.tableData.currentSortDirection =
                    this.tableData.currentSortDirection === "ASC" ? "DESC" : "ASC";
            } else {
                this.tableData.currentSortColumn = columnName;
                this.tableData.currentSortDirection = "ASC";
            }
            this.fetchRecords(
                1,
                this.tableData.currentSortColumn,
                this.tableData.currentSortDirection,
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.ordering
            );
        },
        onRecordsPerPageChange() {
            // Back to page 1 when the number of records per page changes
            this.tableData.currentPage = 1;
            this.tableData.loaderValue = "";
            this.tableData.loaderMessage = this.$loaderMessages.updating;
            this.fetchRecords(
                1,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.onPageRecordNumberChange
            );
        },

        /* ---------------- Columns ---------------- */
        // Columns button: is this column shown?
        isColumnVisible(key) {
            return !this.hiddenColumns.includes(key);
        },

        /* ---------------- Tags ---------------- */
        // Tag list for the Tags boxes (pages with data: usesTags = true, or their own tagsApi)
        async loadTagOptions() {
            if (!this.tagsEndpoint) return;
            try {
                const { response } = await cachedGet(`${this.tagsEndpoint}?action=getTags`, this.$handleConfig(), this.$offlineEnabled);
                if (response.data.status === "success") {
                    this.tagOptions = response.data.results.tags || [];
                }
            } catch (e) {
                // The list stays empty; the rest of the page still works
            }
        },

        /* ---------------- Selecting rows ---------------- */
        toggleSelectAll() {
            if (this.tableData.selectAll) {
                this.tableData.selectedRecordIds = this.tableData.records.map((r) => r.id);
            } else {
                this.tableData.selectedRecordIds = [];
            }
        },

        /* ---------------- Form ---------------- */
        handleInputChange(event) {
            this.$validateField(event.target);
            this.$clearValidationAlert("#recordForm");
        },
        crudImageSelectFunction(e) {
            const file = e.target.files[0];
            if (file) {
                this.formInputs.file = file;
            }
        },
        // Reset button: empty the form but keep the id, so Update still targets the same record.
        // Pages with their own resetForm() keep theirs (a page's method wins over the mixin's).
        resetForm() {
            const id = this.formInputs.id;
            this.formInputs = { ...this.getDefaultFormInputs(), id };
            this.submitted = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = null; // photo/logo box on the form
            this.$clearValidationStates("#recordForm");
        },
        async handleSaveOrEditSuccess(response) {
            // Which record was saved: the server's record_id (new records), or the one being edited
            const savedId = response.data.results?.record_id || this.formInputs?.id || null;

            this.defaultAlert.submitingSavingRecord = false;
            this.handleAlert("ending", response.data.message[0], "success", "page");
            this.$hideModal("recordFormModal");
            await this.fetchRecords();
            this.flashRow(savedId);
        },
        // Makes one table row blink for a moment and scrolls to it if it's off screen.
        // The page's <tr> needs:  :data-record-id="record.id"
        //                         :class="{ 'row-just-saved': isJustSaved(record) }"
        // Not on this page of the table (e.g. sorting put it elsewhere): nothing happens.
        flashRow(id) {
            if (!id) return;
            clearTimeout(this.justSavedTimer);
            this.justSavedRecordId = null; // the same row twice in a row: restart the blink

            this.$nextTick(() => {
                this.justSavedRecordId = String(id);
                this.$nextTick(() => {
                    document.querySelector(`tr[data-record-id="${id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                });
                this.justSavedTimer = setTimeout(() => {
                    this.justSavedRecordId = null;
                }, 3000);
            });
        },
        isJustSaved(record) {
            return this.justSavedRecordId !== null && String(record.id) === this.justSavedRecordId;
        },

        /* ---------------- Delete / restore ---------------- */
        setDeleteType(type) {
            if (type === "soft") {
                this.deleteModalTitle = this.$deleteModalText.modalTitleSoft;
                this.deleteModalMessage = this.$deleteModalText.modalMessageSoft;
                this.tableData.loaderMessage = this.$loaderMessages.deleting;
                this.defaultAlert.alertType = "danger";
            }
            if (type === "restore") {
                this.deleteModalTitle = this.$deleteModalText.modalTitleRestore;
                this.deleteModalMessage = this.$deleteModalText.modalMessageRestore;
                this.tableData.loaderMessage = this.$loaderMessages.restoring;
                this.defaultAlert.alertType = "success";
            }
            if (type === "permanent") {
                this.deleteModalTitle = this.$deleteModalText.modalTitlePermanent;
                this.deleteModalMessage = this.$deleteModalText.modalMessagePermanent;
                this.tableData.loaderMessage = this.$loaderMessages.deletingPermanently;
                this.defaultAlert.alertType = "danger";
            }
            this.tableData.deleteType = type;
        },
        // Delete / restore / delete permanently one record (type set by setDeleteType)
        async deleteRecord(id) {
            this.$showOrHideLoader(this.tableData.loaderMessage);
            const data = { id, deleteType: this.tableData.deleteType, action: "deleteRecord" };

            try {
                const response = await axios.delete(this.apiUrl, this.$handleConfig(data));
                if (response.data.status == "success") {
                    this.handleDeleteRecordSuccess(response);
                    if (typeof this.afterDelete === "function") this.afterDelete(response);
                } else {
                    this.tableData.isVisible = false;
                    this.handleError("ending", response.data.message || response.data, "danger", "page");
                }
            } catch (error) {
                this.handleRequestError(error, "page");
            }
        },
        // Old "Delete Selected" button (pages not yet using the selection bar): asks, then soft deletes
        async deleteSelectedRecords() {
            const ids = this.tableData.selectedRecordIds;
            if (!ids.length) {
                this.handleAlert("ending", "No records selected.", "info", "page");
                return;
            }
            if (!confirm(`Are you sure you want to delete ${ids.length} record(s)?`)) return;
            await this.runBulkAction("soft");
        },
        // Selection bar (TableToolbar @bulk-action): delete or restore every ticked row.
        // The confirm window has already asked.
        async runBulkAction(deleteType = "soft") {
            const ids = this.tableData.selectedRecordIds;
            if (!ids.length) return;

            this.$showOrHideLoader(deleteType === "restore" ? this.$loaderMessages.restoring : this.$loaderMessages.deleting);
            const data = { action: "deleteRecord", ids, deleteType };

            try {
                const response = await axios.delete(this.apiUrl, this.$handleConfig(data));
                if (response.data.status === "success") {
                    this.handleAlert("ending", response.data.message || this.$loaderMessages.deletingSelectedSuccess, "success", "page");
                    this.clearSelection();
                    this.fetchRecords(this.tableData.currentPage, this.tableData.currentSortColumn, "DESC");
                    if (typeof this.afterDelete === "function") this.afterDelete(response);
                } else {
                    this.tableData.isVisible = false;
                    this.handleError("ending", response.data.message, "danger", "page");
                }
            } catch (error) {
                this.handleRequestError(error, "page");
            }
        },
        // Selection bar ✕: untick everything (the normal toolbar comes back)
        clearSelection() {
            this.tableData.selectedRecordIds = [];
            this.tableData.selectAll = false;
        },
        handleDeleteRecordSuccess(response) {
            this.fetchRecords();
            this.handleAlert("ending", response.data.message, this.defaultAlert.alertType, "page");
        },
        handleDeleteSuccess(response) {
            this.handleAlert("ending", response.data.message, "danger", "modal");
        },

        /* ---------------- Import ---------------- */
        onFileImportChange(event) {
            this.formImportData.dataFile = event.target.files[0];
        },
        // Import window: "Import" button
        async importRecords() {
            const d = this.formImportData;
            if (!d.dataFile) return;

            // Pages that ask for the old system name (ImportModal ask-source-system)
            const asksSource = "source_system" in d;
            if (asksSource && !String(d.source_system || "").trim()) {
                this.handleAlert("starting", "Please enter the old system name, e.g. quickbooks.", "danger", "modal");
                return;
            }

            d.importErrors = [];
            this.handleUploadProgress();
            this.handleAlert("starting", this.$loaderMessages.uploading, "success", "modal");

            const formData = new FormData();
            formData.append("file", d.dataFile);
            if (asksSource) formData.append("source_system", String(d.source_system).trim());
            formData.append("action", "importRecords");

            try {
                const response = await axios.post(this.apiUrl, formData, {
                    ...this.$handleConfig(),
                    onUploadProgress: (e) => {
                        if (e.total) d.progress = Math.round((e.loaded * 100) / e.total);
                    },
                });

                const results = response.data.results || {};
                d.importErrors = results.errors || []; // rows not imported (listed in the window)

                // Rows skipped, or imported with notes (e.g. Branches): keep the window open for the list
                const hasProblems = results.skipped_records > 0 || results.has_notes;

                if (response.data.status == "success" && !hasProblems) {
                    // Every row imported
                    this.handleImportRecordsSuccess(response);
                } else if (response.data.status == "success") {
                    // Some imported, some skipped: refresh the table, keep the window open for the list
                    d.message = "";
                    this.fetchRecords(1, this.tableData.currentSortColumn, "DESC");
                    this.handleAlert("ending", response.data.message, "warning", "modal");
                } else {
                    // Nothing imported: show why
                    d.message = "";
                    this.handleAlert("ending", response.data.message, "danger", "modal");
                }
            } catch (error) {
                this.handleRequestError(error, "modal");
            } finally {
                d.uploading = false;
            }
        },
        // Import window: "Download Import Template" button
        async downloadImportTemplate() {
            const d = this.formImportData;
            if (d.templateLoading) return;
            d.templateLoading = true;

            try {
                const response = await axios.get(`${this.apiUrl}?action=downloadTemplate`, this.$handleConfig(null, "blob"));

                // An error comes back as JSON instead of a file
                if (response.data.type && response.data.type.includes("json")) {
                    const data = JSON.parse(await response.data.text());
                    this.handleAlert("ending", data.message, "danger", "modal");
                    return;
                }

                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", this.importTemplateFileName || "import_template.xlsx");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                this.handleAlert("ending", "Unable to download the template. Please try again.", "danger", "modal");
            } finally {
                d.templateLoading = false;
            }
        },
        clearImportForm() {
            this.formImportData.dataFile = null;
            this.formImportData.progress = 0;
            this.formImportData.uploading = false;
            this.formImportData.message = "";
            this.formImportData.messageType = "";
            // The file box is emptied by ImportModal itself (it watches dataFile)
        },
        // Stops the fake progress timer used by import and export
        stopSimulatedProgress() {
            if (this.tableData && this.tableData.simulatedProgressInterval) {
                clearInterval(this.tableData.simulatedProgressInterval);
                this.tableData.simulatedProgressInterval = null;
            }
        },
        handleUploadProgress() {
            this.stopSimulatedProgress(); // never run two timers at once
            this.formImportData.uploading = true;
            this.formImportData.progress = 0;

            // Move the bar slowly while waiting for the server
            this.tableData.simulatedProgressInterval = setInterval(() => {
                if (this.formImportData.progress < 90) {
                    this.formImportData.progress += 1;
                    this.tableData.progress = `${this.formImportData.progress}%`;
                }
            }, 150);
        },
        handleImportRecordsSuccess(response) {
            this.stopSimulatedProgress();
            this.formImportData.message = response.data.message;
            this.formImportData.messageType = "success";
            this.$displayAlert("ending", response.data.message, "success", "page");
            this.$hideModal("importModal");
            this.clearImportForm();
            this.fetchRecords();
        },

        /* ---------------- Export ---------------- */
        // More → Export to Excel / CSV / PDF (same filters as the table)
        async exportRecords(exportFormat) {
            this.handleExportProgress();
            const t = this.tableData;

            const formData = new FormData();
            formData.append("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
            formData.append("page", t.currentPage);
            formData.append("filter", t.recordFilter);
            formData.append("recordsPerPage", t.recordsPerPage);
            formData.append("search", t.searchQuery);
            formData.append("searchBy", t.searchBy);
            this.appendFilterFormData(formData); // dates, statuses, tags ...
            formData.append("currentSortColumn", t.currentSortColumn);
            formData.append("currentSortDirection", t.currentSortDirection);
            formData.append("format", exportFormat);
            // Columns hidden with the Columns button: the export leaves them out too
            formData.append("hiddenColumns", (this.hiddenColumns || []).join(","));
            formData.append("action", "exportRecords");

            try {
                const response = await axios.post(this.apiUrl, formData, {
                    ...this.$handleConfig(null, "blob"),
                    onDownloadProgress: (e) => {
                        if (e.lengthComputable) {
                            const percent = Math.round((e.loaded * 100) / e.total);
                            this.formExportData.exportProgress = percent;
                            t.loaderValue = `${percent}%`;
                        }
                    },
                });
                this.handleExportRecordsSuccess(exportFormat, response);
            } catch (error) {
                this.stopSimulatedProgress();
                // Export failed: don't leave the percentage on the loader for the next load
                this.tableData.loaderValue = "";
                this.tableData.loaderMessage = this.$loaderMessages.loaderMessageDefault;
                this.handleRequestError(error, "page");
            }
        },
        handleExportProgress() {
            this.stopSimulatedProgress(); // never run two timers at once
            this.formExportData.exportProgress = 0;
            this.tableData.loaderMessage = "Exporting" + " ";
            this.tableData.loaderValue = "0%";
            this.$showOrHideLoader(this.tableData.loaderMessage);

            // Move the percentage slowly while waiting for the server
            this.tableData.simulatedProgressInterval = setInterval(() => {
                if (this.formExportData.exportProgress < 90) {
                    this.formExportData.exportProgress += 1;
                    this.tableData.loaderValue = `${this.formExportData.exportProgress}%...`;
                }
            }, 150);
        },
        handleExportRecordsSuccess(exportFormat, response) {
            this.stopSimulatedProgress();
            this.$showOrHideLoader();
            // Export finished: don't leave "100%" on the loader for the next load
            this.tableData.loaderValue = "";
            this.tableData.loaderMessage = this.$loaderMessages.loaderMessageDefault;
            const fileName = this.exportFileName || "export";
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${fileName}.${exportFormat}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
    },
};